<div class="item-create-overlay" *ngIf="fg">
  <div class="item-create-dialog" [class.__seller]="!isPricingRole" (click)="onDialogClick($event)">
    <form [formGroup]="fg">
      <div class="item-wrapper item-detailed-modal item-create">
        <div *ngIf="loader" class="loader"></div>

        <div class="item-header">
          <span class="item-header__title">Create Item</span>
          <div class="close" role="button" (click)="close()"></div>
        </div>

        <!-- Clone provenance (HUBX-8422): the item this form was seeded FROM.
             Rendered only for a clone — a plain Add Item has no source and the
             whole band collapses. The band is a row so the target-company
             selector (sibling sub-task) lands on its right without reshaping
             this card; all three drafts read FROM -> TO. -->
        <div class="clone-band" *ngIf="showBand">
          <div class="clone-source" *ngIf="source">
            <div class="clone-source__head">
              <svg class="clone-source__glyph" width="14" height="14" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="8.5" y="8.5" width="11" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M15.5 5.5H6.5C5.39543 5.5 4.5 6.39543 4.5 7.5V16.5" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round"/>
              </svg>
              <span>Cloning From</span>
            </div>

            <!-- Four facts on one baseline. Each cell is guarded on its own, and
                 an absent fact drops the whole cell rather than leaving a label
                 over a blank: a Seller gets no company, and an item that never
                 went live has no launch date. -->
            <div class="clone-source__facts">
              <div class="clone-source__fact">
                <span class="clone-source__fact-label">Part Number</span>
                <span class="clone-source__fact-value __ident">{{ source.vendorItemCode }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.companyName">
                <span class="clone-source__fact-label">Company</span>
                <span class="clone-source__fact-value __company">{{ source.companyName }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.launchedOn">
                <span class="clone-source__fact-label">Launched</span>
                <span class="clone-source__fact-value">{{ launchedOnLocal | date:'M/d/yy, h:mm a' }}</span>
              </div>
              <div class="clone-source__fact" *ngIf="source.updatedOn">
                <span class="clone-source__fact-label">Updated</span>
                <span class="clone-source__fact-value">{{ updatedOnLocal | date:'M/d/yy, h:mm a' }}</span>
              </div>
            </div>
          </div>

          <!-- FROM -> TO. The arrow is meaningless with only one half present, so it
               renders only when both are. -->
          <div class="clone-band__arrow" *ngIf="source && showTargetSelector" aria-hidden="true">→</div>

          <!-- Target company (ADR-0010, HUBX-8421). Operator roles only. Preselected to
               the company already being acted as, so leaving it alone reproduces the
               request exactly as it was before this control existed; picking another is a
               deliberate cross-company create. Read-only use of the impersonation list —
               choosing here never switches the session's company. -->
          <div class="clone-target" *ngIf="showTargetSelector">
            <div class="clone-target__label">Target Company</div>
            <!-- Purpose-built here rather than reusing the shared app-dropdown: that
                 component only offers its filter box together with `.customer-select`,
                 the 72px black company switcher from the top bar, so search and the
                 top-bar skin cannot be separated. This keeps the closed control
                 identical to the other selects in this popup — same class, same chevron
                 — and adds only what they lack: a company glyph and a search box. -->
            <div class="clone-target__control">
              <button type="button" class="status-select clone-target__toggle"
                [class.__open]="companyPickerOpen" [disabled]="!companies.length"
                (click)="toggleCompanyPicker()">
                <span class="clone-target__value" [class.__placeholder]="!selectedCompanyName">
                  {{ selectedCompanyName || 'Select a company' }}
                </span>
              </button>

              <div class="clone-target__panel" *ngIf="companyPickerOpen">
                <input #companySearch type="text" class="clone-target__search" autocomplete="off"
                  placeholder="Search companies" [(ngModel)]="companyFilter"
                  [ngModelOptions]="{ standalone: true }" (click)="stopPropagation($event)">
                <div class="clone-target__options">
                  <button type="button" class="clone-target__option"
                    *ngFor="let company of filteredCompanies"
                    [class.__selected]="company.vendorId === fg.controls.targetVendorId.value"
                    (click)="selectCompany(company)">
                    {{ company.companyName }}
                  </button>
                  <div class="clone-target__empty" *ngIf="!filteredCompanies.length">No matches</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="item-top-info">

          <!-- LEFT: identity + description on top, taxonomy row (XID / category / manufacturer) below -->
          <div class="item-top-left">

            <div class="item-identity">
              <!-- part number / MPN stacked -->
              <div class="item-id-col">
                <div class="item-info-title">Part Number<span class="req">*</span></div>
                <input type="text" class="form-control" formControlName="vendorItemCode"
                  [class.__invalid]="showFieldError('vendorItemCode')">
                <div class="item-info-title">MPN<span class="req">*</span></div>
                <input type="text" class="form-control" formControlName="mpn"
                  [class.__invalid]="showFieldError('mpn')">
              </div>

              <!-- description -->
              <div class="item-description">
                <div class="item-info-title">Description<span class="req">*</span></div>
                <textarea class="form-control" formControlName="description"
                  [class.__invalid]="showFieldError('description')"></textarea>
              </div>
            </div>

            <!-- taxonomy: XID cell reserved (content ADMIN/VM only) so Manufacturer aligns
                 under Description for every role; the group below spans Description's width -->
            <div class="item-taxonomy">
              <div class="item-labeled-control item-xid">
                <ng-container *ngIf="isPricingRole">
                  <div class="item-info-title">XID</div>
                  <input type="text" class="form-control" formControlName="hubxItemCode"
                    [class.__invalid]="showFieldError('hubxItemCode')">
                </ng-container>
              </div>

              <div class="item-taxonomy-group">
                <div class="item-labeled-control">
                  <div class="item-info-title">Manufacturer<span class="req">*</span></div>
                  <select class="status-select" formControlName="manufacturerId"
                    [class.__invalid]="showFieldError('manufacturerId')">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let man of manufacturers" [value]="man.id">{{ man.name }}</option>
                  </select>
                </div>

                <div class="item-labeled-control">
                  <div class="item-info-title">Category<span class="req">*</span></div>
                  <select class="status-select" formControlName="categoryMainId" (change)="onMainCategoryChange()"
                    [class.__invalid]="fg.controls.categoryId.invalid && fg.controls.categoryMainId.touched">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let cat of categories" [value]="cat.categoryId">{{ cat.name }}</option>
                  </select>
                </div>

                <div class="item-labeled-control" *ngIf="subCategories.length">
                  <div class="item-info-title">Subcategory<span class="req">*</span></div>
                  <select class="status-select" formControlName="categoryId" (change)="onSubCategoryChange()"
                    [class.__invalid]="fg.controls.categoryId.invalid && fg.controls.categoryMainId.touched">
                    <option value="" disabled hidden>Select</option>
                    <option *ngFor="let sub of subCategories" [value]="sub.categoryId">{{ sub.name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Pre-submit category nudge (HUBX-8431). Advisory only: this MPN already
                 matches an item filed elsewhere, which a per-vendor category is allowed
                 to differ from (ADR-0007). It never rings a field and never blocks
                 submit — hence its own quiet amber line under the category selects,
                 deliberately NOT the red validation banner in the footer. -->
            <div class="item-category-nudge" *ngIf="categoryNudge">
              <span>{{ categoryNudge }}</span>
            </div>

          </div>

          <!-- RIGHT: quantities + pricing + DQ -->
          <div class="item-top-right">

            <!-- available / ETA -->
            <div class="item-availability">
              <div class="item-info-title">Available<span class="req">*</span></div>
              <input type="number" class="form-control" formControlName="availability"
                [class.__invalid]="showFieldError('availability')">
              <div class="item-info-title">ETA<span class="req">*</span></div>
              <input type="number" class="form-control" formControlName="leadTimeDays"
                [class.__invalid]="showFieldError('leadTimeDays')">
            </div>

            <!-- MOQ / MXQ — all roles. MOQ is optional (server defaults to 1). -->
            <div class="item-moq-group">
              <div class="item-info-title" [tooltip]="'Minimum Order Quantity'" placement="top">MOQ</div>
              <div class="input-qty">
                <input type="number" class="form-control" formControlName="moq" placeholder="1"
                  [class.__invalid]="showFieldError('moq')">
              </div>
              <div class="item-info-title" [tooltip]="'Maximum Order Quantity'" placement="top">MXQ</div>
              <div class="input-qty">
                <input type="number" class="form-control" formControlName="mxq"
                  [class.__invalid]="showFieldError('mxq') || showGroupError('moqExceedsMxq', 'moq', 'mxq')">
              </div>
            </div>

            <!-- cost / price stacked (Price ADMIN/VM only) -->
            <div class="item-cost-price">
              <div class="item-info-title">Cost<span class="req">*</span></div>
              <div class="input-price">
                <input type="number" class="form-control" formControlName="cost"
                  [class.__invalid]="showFieldError('cost')">
              </div>
              <ng-container *ngIf="isPricingRole">
                <div class="item-info-title">Price<span class="req">*</span></div>
                <div class="input-price">
                  <input type="number" class="form-control" formControlName="unitPrice"
                    [class.__invalid]="showFieldError('unitPrice') || showGroupError('unitPriceBelowCost', 'unitPrice', 'cost')">
                </div>
              </ng-container>
            </div>

            <!-- DQ #1-3 (ADMIN/VM only); the group carries the gap that separates it from Price -->
            <div class="item-dq-group" *ngIf="isPricingRole">
              <div class="item-dq" *ngFor="let dq of dqControls.controls; let i = index" formArrayName="discountQtys">
                <div class="item-info-title" [tooltip]="'Discount Quantity'" placement="top">DQ #{{ i + 1 }}</div>
                <div class="input-group-vertical" [formGroupName]="i">
                  <div class="input-qty" [class.__affixed]="dqHasValue(dq, 'fromQty')">
                    <input type="number" class="form-control" formControlName="fromQty"
                      [class.__invalid]="dqError(i, 'incompletePair') || dqError(i, 'fromQtyBelowMin')">
                  </div>
                  <div class="input-price disc" [class.__affixed]="dqHasValue(dq, 'price')">
                    <input type="number" class="form-control" formControlName="price"
                      [class.__invalid]="dqError(i, 'incompletePair') || dqError(i, 'priceExceedsUnit')">
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="item-bottom-info">
          <!-- attributes + Restrictions/Special Notes chips, single grey band (EXW left of Restrictions) -->
          <div class="item-attributes">

            <div class="item-labeled-control" *ngIf="condition">
              <div class="item-info-title">Condition<span class="req">*</span></div>
              <select class="item-bottom-select input-group-condition" formControlName="condition"
                [class.__invalid]="showFieldError('condition')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let cond of condition.values" [value]="cond.code">{{ cond.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control" *ngIf="warranty">
              <div class="item-info-title">Warranty<span class="req">*</span></div>
              <select class="item-bottom-select input-group-warranty" formControlName="warranty"
                [class.__invalid]="showFieldError('warranty')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let wart of warranty.values" [value]="wart.code">{{ wart.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control" *ngIf="packaging">
              <div class="item-info-title">Packaging<span class="req">*</span></div>
              <select class="item-bottom-select input-group-packaging" formControlName="packaging"
                [class.__invalid]="showFieldError('packaging')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let pckg of packaging.values" [value]="pckg.code">{{ pckg.value }}</option>
              </select>
            </div>

            <div class="item-labeled-control">
              <div class="item-info-title">EXW<span class="req">*</span></div>
              <select class="item-bottom-select input-group-exw" formControlName="exwId"
                [class.__invalid]="showFieldError('exwId')">
                <option value="" disabled hidden>Select</option>
                <option *ngFor="let exw of exwPoints" [value]="exw.id">{{ exw.description }}</option>
              </select>
            </div>

            <!-- Restrictions chip: icon + None/… + Add|Edit/View -->
            <div class="item-labeled-control item-note-control">
              <div class="item-info-title">Restrictions</div>
              <div class="item-note-chip" role="button" (click)="openTextModal('restrictions')"
                [class.has-text]="hasText('restrictions')"
                [tooltip]="hasText('restrictions') ? fg.controls.restrictions.value : ''" placement="top">
                <i class="item-note-chip__icon __restrictions"></i>
                <span class="item-note-chip__state">{{ hasText('restrictions') ? '…' : 'None' }}</span>
                <span class="item-note-chip__action">{{ hasText('restrictions') ? 'Edit/View' : 'Add' }}</span>
              </div>
            </div>

            <!-- Special Notes chip -->
            <div class="item-labeled-control item-note-control">
              <div class="item-info-title">Special Notes</div>
              <div class="item-note-chip" role="button" (click)="openTextModal('specialNotes')"
                [class.has-text]="hasText('specialNotes')"
                [tooltip]="hasText('specialNotes') ? fg.controls.specialNotes.value : ''" placement="top">
                <i class="item-note-chip__icon __notes"></i>
                <span class="item-note-chip__state">{{ hasText('specialNotes') ? '…' : 'None' }}</span>
                <span class="item-note-chip__action">{{ hasText('specialNotes') ? 'Edit/View' : 'Add' }}</span>
              </div>
            </div>

          </div>
        </div>

        <div class="item-create-footer">
          <!-- status → CreateApproved flag only (ADR-0009 rule 5); parked here so it doesn't claim a top-row column.
               SELLER: locked to Pending (lock glyph + long hint on hover); ADMIN/VM: Pending/Active selectable. -->
          <div class="item-status">
            <div class="item-info-title">Status<span class="req">*</span></div>
            <div class="item-status-row">
              <select class="status-select" formControlName="status"
                [tooltip]="isPricingRole ? '' : sellerStatusHint" placement="top">
                <option value="Pending">Pending</option>
                <option value="Active" *ngIf="isPricingRole">Active</option>
              </select>
              <div class="item-status-hint">
                <span class="item-status-hint__dot" [class.__active]="fg.controls.status.value === 'Active'"></span>
                <span>{{ statusHint }}</span>
              </div>
            </div>
          </div>
          <div class="line-error" *ngIf="lookupsError">
            <span>Could not load form data. Close and try again.</span>
          </div>
          <button type="button" class="item-create-footer__cancel" (click)="close()">Cancel</button>
          <button type="button" class="item-create-footer__submit" [disabled]="loader || lookupsError"
            (click)="submit()">
            <span>Create Item</span>
          </button>

          <!-- validation banner (item-details pattern): field rings show WHERE, this
               line says WHAT. Own line at the bottom, below Status and the actions. -->
          <div class="item-error-banner" *ngIf="bannerError">
            <span>{{ bannerError }}</span>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>

<app-item-text-modal *ngIf="textModalField"
  [title]="textModalTitle"
  [value]="textModalValue"
  (saved)="onTextModalSaved($event)"
  (closedModal)="onTextModalClosed()">
</app-item-text-modal>
